import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { reportsAPI, leadsAPI, invoicesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  DollarSign, 
  Clock, 
  TrendingUp,
  FileText,
  ArrowRight,
  AlertTriangle,
  Calendar,
  Phone
} from 'lucide-react';

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [expiringContracts, setExpiringContracts] = useState({ expiring_soon: [], already_expired: [] });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, leadsRes, invoicesRes, contractsRes] = await Promise.all([
        reportsAPI.getSummary(),
        leadsAPI.getAll({ status: 'new' }),
        invoicesAPI.getAll({ status: 'pending' }),
        reportsAPI.getExpiringContracts(30)
      ]);
      
      setSummary(summaryRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
      setPendingInvoices(invoicesRes.data.slice(0, 5));
      setExpiringContracts(contractsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const totalContractAlerts = (expiringContracts.expiring_soon?.length || 0) + (expiringContracts.already_expired?.length || 0);

  const stats = [
    {
      title: 'Active Customers',
      value: summary?.total_customers || 0,
      icon: Users,
      color: 'text-lime-600',
      bgColor: 'bg-lime-100'
    },
    {
      title: 'New Leads',
      value: summary?.new_leads || 0,
      icon: UserPlus,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Monthly Revenue',
      value: `$${(summary?.monthly_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Contract Alerts',
      value: totalContractAlerts,
      icon: AlertTriangle,
      color: totalContractAlerts > 0 ? 'text-orange-600' : 'text-gray-400',
      bgColor: totalContractAlerts > 0 ? 'bg-orange-100' : 'bg-gray-100'
    }
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome back! Here's your business overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="border-0 shadow-sm" data-testid={`stat-card-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Contract Expiration Alerts */}
      {totalContractAlerts > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-orange-500">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Contract Expiration Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Expired Contracts */}
              {expiringContracts.already_expired?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    Expired Contracts ({expiringContracts.already_expired.length})
                  </h4>
                  <div className="space-y-2">
                    {expiringContracts.already_expired.slice(0, 5).map((customer) => (
                      <div key={customer.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-medium text-gray-900">{customer.contact_name}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {customer.phone}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-red-600">
                            Expired {Math.abs(customer.days_until_expiry)} days ago
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                            <Calendar className="w-3 h-3" /> {customer.contract_end_date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expiring Soon */}
              {expiringContracts.expiring_soon?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-orange-600 mb-2 flex items-center gap-1">
                    <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                    Expiring Within 30 Days ({expiringContracts.expiring_soon.length})
                  </h4>
                  <div className="space-y-2">
                    {expiringContracts.expiring_soon.slice(0, 5).map((customer) => (
                      <div key={customer.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-medium text-gray-900">{customer.contact_name}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {customer.phone}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-orange-600">
                            {customer.days_until_expiry === 0 
                              ? 'Expires today!' 
                              : `${customer.days_until_expiry} days left`}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                            <Calendar className="w-3 h-3" /> {customer.contract_end_date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Link to="/admin/customers">
                <Button variant="outline" className="w-full mt-2">
                  View All Customers <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg. Days to Payment</p>
                <p className="text-2xl font-bold text-gray-900">{summary?.avg_days_to_payment || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Paid Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{summary?.paid_invoices_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-lime-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-lime-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${(summary?.total_revenue || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* New Leads */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">New Leads</CardTitle>
            <Link to="/admin/leads">
              <Button variant="ghost" size="sm" className="text-lime-600 hover:text-lime-700">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentLeads.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No new leads</p>
            ) : (
              <div className="space-y-4">
                {recentLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{lead.name}</p>
                      <p className="text-sm text-gray-500">{lead.phone}</p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 capitalize">
                      {lead.inquiry_type.replace('_', ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Invoices */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Pending Invoices</CardTitle>
            <Link to="/admin/invoices">
              <Button variant="ghost" size="sm" className="text-lime-600 hover:text-lime-700">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {pendingInvoices.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No pending invoices</p>
            ) : (
              <div className="space-y-4">
                {pendingInvoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                      <p className="text-sm text-gray-500">{invoice.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lime-600">${invoice.amount.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
